import React, { useEffect, useState } from 'react';
import {
  useNavigate } from 'react-router-dom';
import { Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button } from '@mui/material';
import { getAuth } from 'firebase/auth';
import { doc,
  getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet';
import { ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { fetchSupplements } from '../Utils';
import config from '../config';
import SupplementsTable from './SupplementsTable';
import ProFeature from './common/ProFeature';
import { ShieldIcon } from '../icons';

function MedicationSupportView() {
  const [stack, setStack] = useState(null);
  const [supplementMap, setSupplementMap] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    let isMounted = true;

    async function fetchStackAndSupplements() {
      setLoading(true);
      setError('');
      
      const user = auth.currentUser;
      if (!user) {
        setError('You must be logged in to view your medication support stack.');
        setLoading(false);
        return;
      }

      try {
        // Fetch supplements and medication support stack in parallel
        const [supplementsList, systemStackDoc] = await Promise.all([
          fetchSupplements(config),
          getDoc(doc(db, 'users', user.uid, 'system_stacks', 'medication_support'))
        ]);

        // Process supplements
        const newSupplementMap = {};
        if (Array.isArray(supplementsList)) {
          supplementsList.forEach(sup => {
            newSupplementMap[sup.key] = sup.name;
          });
        } else {
          console.error("MedicationSupportView: Fetched supplements data is not an array:", supplementsList);
        }
        if (isMounted) {
          setSupplementMap(newSupplementMap);
        }

        // Process system stack
        if (systemStackDoc.exists()) {
          const data = systemStackDoc.data();
          if (isMounted && data && data.stackData) {
            setStack({
              ...data.stackData,
              isSystemGenerated: true,
              created_at: data.createdAt,
              updated_at: data.updatedAt
            });
          }
        } else {
          setError('No medication support stack found. Add medications to your profile to generate recommendations.');
        }

      } catch (err) {
        console.error('Error fetching medication support stack:', err);
        setError('Failed to load your medication support stack. Please try again.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchStackAndSupplements();

    return () => {
      isMounted = false;
    };
  }, [auth.currentUser]);

  const handleMainLinkClick = (supplementId, e) => {
    e.preventDefault();
    navigate(`/supplements/${supplementId}`);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Helmet>
          <title>Replenish Stack - My Stack</title>
        </Helmet>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{ mb: 2 }}
          >
            Back to Dashboard
          </Button>
        </Box>
        <Alert severity="info" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <ProFeature 
      feature="medication_support"
      upgradeTitle="Replenish Stacks"
      upgradeDescription="Get personalized supplement recommendations based on your current medications to counteract nutrient depletions."
    >
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Helmet>
          <title>{stack?.name || 'Replenish Stack'} - My Stack</title>
          <meta name="description" content="Your personalized medication support supplement recommendations" />
        </Helmet>

        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{ mb: 2 }}
          >
            Back to Dashboard
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ShieldIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              {stack?.name || 'Replenish Stack'}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            This stack was automatically generated based on your current medications to help support your nutritional needs. 
            The recommendations are designed to counteract potential nutrient depletions caused by your medications.
          </Alert>
        </Box>

        {stack && stack.supplements && stack.supplements.length > 0 ? (
          <SupplementsTable 
            supplements={stack.supplements} 
            supplementMap={supplementMap}
            readOnly={true}
            handleMainLinkClick={handleMainLinkClick}
          />
        ) : (
          <Alert severity="warning">
            No supplement recommendations found in this stack.
          </Alert>
        )}

        <Box sx={{ mt: 4, p: 3, backgroundColor: theme.palette.grey[50], borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            About This Stack
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            This medication support stack is automatically updated whenever you modify your medication list in your profile. 
            The recommendations are based on known nutrient interactions and depletions associated with your specific medications.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Important:</strong> These are general recommendations. Always consult with your healthcare provider before 
            starting any new supplements, especially if you have medical conditions or take multiple medications.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last updated: {stack?.updated_at ? new Date(stack.updated_at).toLocaleDateString() : 'Unknown'}
          </Typography>
        </Box>
      </Container>
    </ProFeature>
  );
}

export default MedicationSupportView; 